import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';
import ProductCardMinimal from 'components/Export/Components/ProductCardMinimal';
import i18n from 'i18n';
import { ImageExtension, Mark, ProductImage } from 'microshop-api';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import getImage, { ImageType } from 'utils/getImage';
import { groupBy } from 'utils/groupBy';
import PdfPageTitle from '../Components/PdfPageTitle';
import ExportPdfContext from '../ExportPdfContext';
import { PdfPageInfo, PdfProduct } from '../exportPdfTypes';
import { PdfPageWithFooter } from './PdfPageWithFooter';

const styles = StyleSheet.create({
    careImageWrapper: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        paddingHorizontal: 8,
        marginBottom: 8,
    },
    careImageTextWrapper: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        paddingHorizontal: 8,
        marginBottom: 8,
    },
    careImage: {
        width: 20,
        height: 'auto',
        paddingRight: 2,
    },
    careText: {
        fontFamily: 'PDF Font',
        fontSize: '8px',
    },
});
type Props = {
    products: PdfProduct[];
    // priceSettings: PdfPriceSettings;
};

const PdfProductsPageCompact = ({ products, seller, orderNumber }: PdfPageInfo & Props) => {
    const { t } = useTranslation();
    const { orientation, pageStartingNumber } = useContext(ExportPdfContext);

    if (products && products.length < 1) return null;

    return (
        <PdfPageWithFooter seller={seller} fill orderNumber={orderNumber}>
            {!pageStartingNumber && <PdfPageTitle>{t('products', 'Products')}</PdfPageTitle>}
            <View style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                {products?.map((p) =>
                    p.variations?.map((v, i) => {
                        return (
                            <ProductCardMinimal
                                orientation={orientation}
                                key={`oppp-${p.productNumber}${i}`}
                                image={getImage(v.image ?? undefined, ImageType.LargeThumbNail, ImageExtension.Jpg)}
                                productBrandName={`${p.productBrandName}`}
                                productNumber={`${v.variationNumber}`}
                                productName={`${p.productName}${v.color ? ` - ${v.color}` : ''}`}
                                price={v.price?.customer?.str ?? p.price?.customer?.str}
                                description={p.description ?? ''}
                                details={getDetails(p)}
                                additionalInfo={getCareImgs(p.marks?.careInstructions ?? [])}
                            />
                        );
                    }),
                )}
            </View>
        </PdfPageWithFooter>
    );
};

export default PdfProductsPageCompact;

function getCareImgs(careInstructions?: Mark[]) {
    const sortedCareInstructions = groupBy([...(careInstructions ?? [])], (c) =>
        !!c.image?.fileName ? 'image' : 'noImage',
    );

    const icons = sortedCareInstructions.get('image');
    const texts = sortedCareInstructions.get('noImage');

    return (
        <View>
            {icons && (
                <View style={styles.careImageWrapper}>
                    {icons.map((careInstruction) => {
                        const imgKey = careInstruction?.imageKey;
                        const fixedFileExtension: ProductImage = {
                            ...careInstruction.image,
                            fileName: careInstruction.image?.fileName?.split('.')?.[0],
                        };
                        const image =
                            careInstruction.image?.fileName &&
                            getImage(fixedFileExtension, ImageType.ThumbNail, ImageExtension.Png);

                        if (image) {
                            return <Image cache={true} key={imgKey} style={styles.careImage} src={image} />;
                        }
                        return null;
                    })}
                </View>
            )}
            {texts && (
                <View style={styles.careImageTextWrapper}>
                    {texts.map((careInstruction) => (
                        <View key={careInstruction.imageKey} style={styles.careText}>
                            <Text>{careInstruction.text}</Text>
                        </View>
                    ))}
                </View>
            )}
        </View>
    );
}

function getDetails(p: PdfProduct) {
    const fabrics = i18n.t('fabrics', 'Material');
    const weight = i18n.t('weight', 'Weight');
    const certifications = i18n.t('certificates', 'Certifications');
    const certificationDescriptions = p.attributes?.certificationDescription?.join(', ');
    const certification = p.marks?.certifications?.map((c) => `${c.text}`).join(', ');

    const details = [
        {
            heading: fabrics,
            info: p.attributes?.fabrics?.join(',') ?? '',
        },
        {
            heading: weight,
            info: p.attributes?.weight?.join(',') ?? '',
        },
    ];
    if (certification) {
        details.push({
            heading: certifications,
            info: `${certification} ${certificationDescriptions ? `(${certificationDescriptions})` : ''}`,
        });
    }

    return details;
}
